import React, { useState, useMemo } from 'react';
import './AvailabilityCalendar.css';

// Utilidades
function pad(n) { return n < 10 ? `0${n}` : String(n); }
function toKey(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
// Noites ocupadas de uma reserva: [checkin, checkout) — o dia do checkout fica
// livre para o check-in da reserva seguinte (padrão hoteleiro).
function* daysOccupied(start, end) {
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  for (let dt = s; dt < e; dt.setDate(dt.getDate() + 1)) {
    yield new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  }
}

function getDaysArray(year, month) {
  const numDays = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: numDays }, (_, i) => i + 1);
}

const months = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const weekDays = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];

export default function AvailabilityCalendar({
  reservedRanges = [], // [{start: Date, end: Date, status: 'booked'|'pending'}]
  minNights = 3,
  maxNights = 60,
  initialDate = new Date(),
  title = 'Disponibilidade',
  onRangeSelect
}) {
  const [current, setCurrent] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  const [selectStart, setSelectStart] = useState(null);
  const [selectEnd, setSelectEnd] = useState(null);

  // Mapa de status por dia
  const dayStatus = useMemo(() => {
    const map = {};
    reservedRanges.forEach(({ start, end, status }) => {
      if (!(start instanceof Date) || !(end instanceof Date)) return;
      if (end < start) return;
      for (const dt of daysOccupied(start, end)) {
        const k = toKey(dt);
        const s = String(status || '').toLowerCase();
        map[k] = s.includes('pend') ? 'pending' : 'booked';
      }
    });
    return map;
  }, [reservedRanges]);

  function isBlocked(date) {
    const k = toKey(date);
    return dayStatus[k] === 'booked' || dayStatus[k] === 'pending';
  }

  function canSelectRange(a, b) {
    if (!a || !b) return false;
    const start = a < b ? a : b;
    const end = a < b ? b : a;
    // não atravessar dias bloqueados (checkout é livre: só noites ocupadas contam)
    for (const dt of daysOccupied(start, end)) {
      if (isBlocked(dt)) return false;
    }
    return true;
  }

  function nextMonth() {
    setCurrent(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }
  function prevMonth() {
    setCurrent(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }

  function renderMonth(year, month) {
    const days = getDaysArray(year, month);
    const firstDay = new Date(year, month, 1).getDay();
    const monthName = months[month];
    return (
      <div className="calendar-month" key={`${year}-${month}`}> 
        <div className="calendar-header">{monthName} {year}</div>
        <div className="calendar-weekdays">
          {weekDays.map((w, i) => <div key={i} className="calendar-weekday">{w}</div>)}
        </div>
        <div className="calendar-days">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="calendar-day empty"></div>
          ))}
          {days.map(day => {
            const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
            const dateObj = new Date(year, month, day);
            let status = dayStatus[dateStr] || 'available';
            const isSelected = selectStart && selectEnd && dateObj >= selectStart && dateObj <= selectEnd;
            return (
              <div
                key={dateStr}
                className={`calendar-day ${status} ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  if (status !== 'available') return;
                  if (!selectStart) {
                    setSelectStart(dateObj);
                    setSelectEnd(null);
                  } else if (!selectEnd) {
                    // escolher fim
                    const a = selectStart;
                    const b = dateObj;
                    if (!canSelectRange(a, b)) {
                      // reinicia seleção
                      setSelectStart(dateObj);
                      setSelectEnd(null);
                      return;
                    }
                    const start = a < b ? a : b;
                    const end = a < b ? b : a;
                    setSelectStart(start);
                    setSelectEnd(end);
                    onRangeSelect && onRangeSelect(start, end);
                  } else {
                    // iniciar nova seleção
                    setSelectStart(dateObj);
                    setSelectEnd(null);
                  }
                }}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Render two months: current and next
  const year = current.getFullYear();
  const month = current.getMonth();
  const next = new Date(year, month + 1, 1);

  return (
    <div className="availability-calendar-wrapper">
      <div className="calendar-controls">
        <button onClick={prevMonth}>{'<'}</button>
        <span className="calendar-title">{title}</span>
        <button onClick={nextMonth}>{'>'}</button>
      </div>
      <div className="calendar-months">
        {renderMonth(year, month)}
        {renderMonth(next.getFullYear(), next.getMonth())}
      </div>
      <div className="calendar-legend">
        <span className="legend available">Disponível</span>
        <span className="legend pending">Pendente</span>
        <span className="legend booked">Reservado</span>
      </div>
      <div className="calendar-info">
        <span>Estadia mínima: <b>{minNights} noite(s)</b></span>
        <span>Estadia máxima: <b>{maxNights} noite(s)</b></span>
      </div>
    </div>
  );
}
